import { createContext, useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { getEmotionRoomFromDatabase, saveEmotionRoomToDatabase } from "../hooks/api";

const EmotionRoomContext = createContext();

export const EmotionRoomProvider = ({ children }) => {
  const [emotionRoomData, setEmotionRoomData] = useState(() => {
    const stored = localStorage.getItem("childEmotionRoom");
    return stored
      ? JSON.parse(stored)
      : { itemsArray: [], itemPositions: {} };
  });

  const isHydratedRef = useRef(false);

  const loadEmotionRoom = useRef((childId) => {
    isHydratedRef.current = false;
    getEmotionRoomFromDatabase(childId)
      .then((data) => {
        const fresh = {
          itemsArray:    data.emotion_items          ?? [],
          itemPositions: data.emotion_item_positions ?? {},
        };
        // Confiar siempre en lo que devuelve el backend (aunque venga vacío) — si no, un
        // niño con el cuarto realmente vacío hereda la decoración cacheada de otro niño que
        // se logueó antes en el mismo dispositivo (misma key de localStorage sin child_id).
        setEmotionRoomData(fresh);
        localStorage.setItem("childEmotionRoom", JSON.stringify(fresh));
      })
      .catch((err) => console.error("No se pudo cargar el cuarto de emociones:", err))
      .finally(() => { isHydratedRef.current = true; });
  });

  useEffect(() => {
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    loadEmotionRoom.current(childId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("childId")]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "childId") return;
      if (e.newValue) {
        loadEmotionRoom.current(e.newValue);
      } else {
        isHydratedRef.current = false;
        setEmotionRoomData({ itemsArray: [], itemPositions: {} });
        localStorage.removeItem("childEmotionRoom");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    localStorage.setItem("childEmotionRoom", JSON.stringify(emotionRoomData));
    if (!isHydratedRef.current) return;
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    saveEmotionRoomToDatabase(emotionRoomData.itemsArray, emotionRoomData.itemPositions || {})
      .catch((err) => console.error("Error al guardar el cuarto de emociones:", err));
  }, [emotionRoomData]);

  const value = useMemo(() => ({ emotionRoomData, setEmotionRoomData }), [emotionRoomData]);

  return (
    <EmotionRoomContext.Provider value={value}>
      {children}
    </EmotionRoomContext.Provider>
  );
};

EmotionRoomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useEmotionRoom = () => useContext(EmotionRoomContext);
